// Cầu nối frontend ↔ backend /lesson-builder.
// Khi ĐÃ đăng nhập (có JWT) → gọi API thật; khi chưa/đứt mạng → fallback localStorage
// để vẫn dùng/test được offline. Giữ nguyên shape LessonDoc/CourseDoc.
import { API_CONFIG } from '../../../config/api.config'
import { lessonStorage } from '../lib/lessonStorage'
import { courseStorage } from '../lib/courseStorage'
import type { LessonDoc } from '../types/LessonDoc'
import type { CourseDoc } from '../types/CourseDoc'

const BASE = `${API_CONFIG.python.apiUrl}/lesson-builder`

function getToken(): string {
  return (
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    ''
  )
}

export function isAuthed(): boolean {
  return !!getToken()
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers || {}),
    },
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${detail}`)
  }
  return res.json()
}

// ---------------- Lessons ----------------
export const lessonApi = {
  async list(): Promise<LessonDoc[]> {
    if (!isAuthed()) return lessonStorage.list()
    try {
      return await req<LessonDoc[]>('/lessons')
    } catch {
      return lessonStorage.list()
    }
  },
  async sharedWithMe(): Promise<LessonDoc[]> {
    if (!isAuthed()) return []
    try {
      return await req<LessonDoc[]>('/lessons/shared')
    } catch {
      return []
    }
  },
  async get(id: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.get(id)
    try {
      return await req<LessonDoc>(`/lessons/${id}`)
    } catch {
      return lessonStorage.get(id)
    }
  },
  async getBySlug(slug: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.getBySlug(slug)
    try {
      return await req<LessonDoc>(`/lessons/by-slug/${encodeURIComponent(slug)}`)
    } catch {
      return lessonStorage.getBySlug(slug)
    }
  },
  async save(doc: LessonDoc): Promise<LessonDoc> {
    if (!isAuthed()) return lessonStorage.save(doc)
    try {
      return await req<LessonDoc>('/lessons', { method: 'POST', body: JSON.stringify(doc) })
    } catch {
      return lessonStorage.save(doc)
    }
  },
  async publish(id: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.publish(id)
    try {
      return await req<LessonDoc>(`/lessons/${id}/publish`, { method: 'POST' })
    } catch {
      return lessonStorage.publish(id)
    }
  },
  async remove(id: string): Promise<void> {
    if (!isAuthed()) {
      lessonStorage.remove(id)
      return
    }
    try {
      await req(`/lessons/${id}`, { method: 'DELETE' })
    } catch {
      lessonStorage.remove(id)
    }
  },
}

// ---------------- Courses ----------------
export const courseApi = {
  async list(): Promise<CourseDoc[]> {
    if (!isAuthed()) return courseStorage.list()
    try {
      return await req<CourseDoc[]>('/courses')
    } catch {
      return courseStorage.list()
    }
  },
  async sharedWithMe(): Promise<CourseDoc[]> {
    if (!isAuthed()) return []
    try {
      return await req<CourseDoc[]>('/courses/shared')
    } catch {
      return []
    }
  },
  async get(id: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.get(id)
    try {
      return await req<CourseDoc>(`/courses/${id}`)
    } catch {
      return courseStorage.get(id)
    }
  },
  async getBySlug(slug: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.getBySlug(slug)
    try {
      return await req<CourseDoc>(`/courses/by-slug/${encodeURIComponent(slug)}`)
    } catch {
      return courseStorage.getBySlug(slug)
    }
  },
  async save(doc: CourseDoc): Promise<CourseDoc> {
    if (!isAuthed()) return courseStorage.save(doc)
    try {
      return await req<CourseDoc>('/courses', { method: 'POST', body: JSON.stringify(doc) })
    } catch {
      return courseStorage.save(doc)
    }
  },
  async publish(id: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.publish(id)
    try {
      return await req<CourseDoc>(`/courses/${id}/publish`, { method: 'POST' })
    } catch {
      return courseStorage.publish(id)
    }
  },
  async remove(id: string): Promise<void> {
    if (!isAuthed()) {
      courseStorage.remove(id)
      return
    }
    try {
      await req(`/courses/${id}`, { method: 'DELETE' })
    } catch {
      courseStorage.remove(id)
    }
  },
}

// ---------------- Sharing ----------------
export interface SharePayload {
  resource_type: 'lesson' | 'course'
  resource_id: string
  scope: 'all' | 'teacher'
  target_email?: string
}

export const shareApi = {
  async share(payload: SharePayload): Promise<{ id: string; ok: boolean }> {
    return req('/share', { method: 'POST', body: JSON.stringify(payload) })
  },
}

// ---------------- Kết quả học tập ----------------
export interface ResultPayload {
  card_id?: string
  lesson_slug?: string
  score: number
  max_score: number
  total: number
  correct: number
  detail?: any
}

export const resultApi = {
  // Nộp kết quả (cần đăng nhập). Không đăng nhập → bỏ qua, vẫn chấm phía client.
  async submit(lessonId: string, payload: ResultPayload): Promise<void> {
    if (!isAuthed() || !lessonId) return
    try {
      await req(`/lessons/${lessonId}/submit`, { method: 'POST', body: JSON.stringify(payload) })
    } catch {
      /* không chặn trải nghiệm học nếu lưu lỗi */
    }
  },
  // Danh sách kết quả (chủ bài / admin)
  async list(lessonId: string): Promise<any[]> {
    try {
      return await req(`/lessons/${lessonId}/results`)
    } catch {
      return []
    }
  },
}

// ---------------- Word → Bài học AI (Smart Course Converter) ----------------
// Gửi .docx lên server: parser pmaker (MathType→LaTeX, tách ảnh) + AI bám sát tài liệu
// → trả ContentItem[] (gồm block quiz tương tác) để tạo thẻ "rich" render bằng ContentRenderer.
export async function wordToLessonItems(
  file: File
): Promise<{ items: any[]; title: string; image_count: number }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${BASE}/word-to-lesson`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  })
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${t}`)
  }
  return res.json()
}
